import { Component, Element, h, Host, Method, Prop, State, Watch } from "@stencil/core";
import { getSvgContent, iconContent } from "./request";
import { getIconRegistry } from "./registry";
import { debounce } from "../../utils/debounce";

/**
 * ux-icon is a flexible SVG icon web component for displaying icons with responsive sizing and color options.
 *
 * @slot (default) - Not to be used directly. Its purpose is to prevent icon duplication when the web component is cloned.
 *
 * @example
 * <ux-icon name="checkmark" fill="primary" sizeS={16} sizeM={24} sizeL={32} />
 *
 * @remarks
 * This component is heavily inspired by https://github.com/ionic-team/ionicons
 */
@Component({
    tag: "ux-icon",
    styleUrl: "icon.scss",
    shadow: true
})
export class Icon {
    /** Reference to the host element */
    @Element() iconElement!: HTMLUxIconElement;

    /**
     * Name of the icon to display. Should match the icon name registered in the icon registry.
     */
    @Prop({ reflect: true }) readonly name: string = "";

    /**
     * Contains the full SVG markup for the icon. Managed internally.
     * @internal
     */
    // @ts-expect-error Check for typing
    @State() private svgContent: string = null;

    /**
     * The computed icon size in pixels, based on viewport and props.
     * @internal
     */
    @State() iconDimension: number;

    /**
     * Supported values for the `fill` prop. Can be expanded if needed.
     * @internal
     */
    private supportedFills: string[] = ["primary", "white", "black"];

    /**
     * Sets the color of the icon. Supported values: "primary", "white", "black".
     */
    @Prop() readonly fill: string = "";

    /**
     * If true, selects the filled version of the icon (if available).
     */
    @Prop() readonly filled: boolean = false;

    /**
     * Icon size (in px) for mobile viewports.
     */
    @Prop() readonly sizeS: number | null = null;

    /**
     * Icon size (in px) for tablet viewports.
     */
    @Prop() readonly sizeM: number | null = null;

    /**
     * Icon size (in px) for desktop viewports.
     */
    @Prop() readonly sizeL: number | null = null;

    /**
     * Weight of the icon: "light", "normal", or "bold". Defaults to normal.
     */
    // @ts-expect-error Check for typing
    @Prop() readonly weight: string = null;

    /** Media query watcher for tablet viewports */
    private tabletMediaWatcher = window.matchMedia("(min-width: 768px)");
    /** Media query watcher for desktop viewports */
    private desktopMediaWatcher = window.matchMedia("(min-width: 1024px)");

    /**
     * Checks if any custom dimensions are set via props.
     * @returns {boolean}
     */
    private hasCustomDimensions = () => {
        return this.sizeS !== null || this.sizeM !== null || this.sizeL !== null;
    };

    /**
     * Updates the iconDimension state based on current viewport and custom size props.
     */
    private updateIconDimension = () => {
        if (!this.hasCustomDimensions()) {
            return;
        }
        if (this.desktopMediaWatcher.matches) {
            // @ts-expect-error Check for typing
            this.iconDimension = this.sizeL || this.sizeM || this.sizeS;
        } else if (this.tabletMediaWatcher.matches) {
            // @ts-expect-error Check for typing
            this.iconDimension = this.sizeM || this.sizeS || this.sizeL;
        } else {
            // @ts-expect-error Check for typing
            this.iconDimension = this.sizeS || this.sizeM || this.sizeL;
        }
    };

    /**
     * Lifecycle: Runs before the component loads. Sets initial icon dimension and loads the icon.
     */
    componentWillLoad() {
        this.updateIconDimension();
        this.loadIcon();
    }

    /**
     * Lifecycle: Runs when the component is added to the DOM. Sets up media query listeners.
     */
    connectedCallback() {
        this.tabletMediaWatcher.addEventListener("change", this.updateIconDimension);
        this.desktopMediaWatcher.addEventListener("change", this.updateIconDimension);
    }

    /**
     * Lifecycle: Runs when the component is removed from the DOM. Cleans up media query listeners.
     */
    disconnectedCallback() {
        this.tabletMediaWatcher.removeEventListener("change", this.updateIconDimension);
        this.desktopMediaWatcher.removeEventListener("change", this.updateIconDimension);
    }

    /**
     * Reloads the icon asset. Called from the IconRegistry when the assetsPath has changed.
     */
    @Method()
    async reload(): Promise<void> {
        this.loadIconDebounced();
    }

    /**
     * Watches for changes to name, filled, or weight props and reloads the icon if they change.
     */
    @Watch("name")
    @Watch("filled")
    @Watch("weight")
    updateIcon() {
        this.loadIconDebounced();
    }

    /**
     * Loads the SVG content for the icon, either synchronously (if cached) or asynchronously.
     * Handles missing icons with a warning (only once per icon name).
     */
    private loadIcon = (): void => {
        if (this.name === "") {
            // @ts-expect-error Check for typing
            this.svgContent = null;
            return;
        }
        const url = getIconRegistry().resolveAssetUrl(this);
        if (url === null) {
            // @ts-expect-error Check for typing
            this.svgContent = null;
            if (loggedWarnings.includes(this.name)) {
                return;
            }
            /* eslint-disable-next-line no-console */
            console.warn(`Icon ${this.name} is not available in ux-icon.`);
            loggedWarnings.push(this.name);
            return;
        }
        if (iconContent.has(url)) {
            /*
             * Synchronous load if already cached
             */
            /* @ts-expect-error Check for typing */
            this.svgContent = iconContent.get(url);
        } else {
            // Asynchronous load if not cached
            getSvgContent(url)
                // @ts-expect-error Check for typing
                .then(() => (this.svgContent = iconContent.get(url)))
                .catch(() => {
                    /* eslint-disable-next-line no-console */
                    console.warn(`Icon ${this.name} could not be loaded from ${url}`);
                });
        }
    };

    /**
     * Debounced version of loadIcon to avoid excessive reloads.
     */
    private loadIconDebounced = debounce(this.loadIcon, 100);

    /**
     * Renders the icon. Applies custom sizing and fill classes as needed.
     */
    render() {
        if (this.name === "") {
            return null;
        }
        const customCss = this.hasCustomDimensions() ? { fontSize: `${this.iconDimension}px` } : null;
        return (
            <Host
                class={{
                    ["ux-icon"]: true,
                    [`ux-icon--${this.fill}`]: this.supportedFills.includes(this.fill)
                }}
                // @ts-expect-error Check for typing
                style={customCss}
            >
                <div class="ux-icon__inner" innerHTML={this.svgContent} />
                <div class="ux-icon__slot">
                    <slot />
                </div>
            </Host>
        );
    }
}

/**
 * Holds logged warnings about unknown icons to prevent duplicate warnings.
 * @internal
 */
const loggedWarnings: string[] = [];
